import ks from "../ReUsableTS/kBrowserShared";
import { kCellEditCommand, kEditableTableActive } from "./kEditableTableTypes";
import type { kHTMLEditableColType, kRowFormFieldControl } from "./kEditableTableTypes";
import { kEditableColumn } from "./kEditableColumn";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTableRow } from "./kEditableTableRow";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableLifecycle {
    public static Destroy(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            window.clearInterval(Self["_IntervalId"]);
            Self.CancelPendingFilter();
            document.removeEventListener("keydown", Self.KeyDown);
            if (Self.Table) {
                Self.Table.removeEventListener("paste", Self.PasteHandler);
                Self.Table.removeEventListener("mouseenter", Self.MouseEnter);
                Self.Table.removeEventListener("focus", Self.OnTableFocus);
            }
            if (kEditableTableActive.ActiveTable === Self) {
                kEditableTableActive.ActiveTable = null;
            }
            // Release the filter UI's own listeners (the anchored popup wires
            // window-level capture handlers that outlive the grid otherwise).
            if (Self.FilterUI !== null) { Self.FilterUI.Destroy(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.Destroy");
        }
    }

    public static RecordEdit(Self: kEditableTable, Cell: kEditableTableCell, OldValue: any, NewValue: any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_RedoStack"] = [];
            Self["_UndoStack"].push(new kCellEditCommand(Cell, OldValue, NewValue));
            if (Self["_UndoStack"].length > Self.MaxUndoSteps) {
                Self["_UndoStack"].shift();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.RecordEdit");
        }
    }

    public static Undo(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Cmd: kCellEditCommand | undefined = Self["_UndoStack"].pop();
            if (Cmd) {
                Self["_RedoStack"].push(Cmd);
                Cmd.Undo();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.Undo");
        }
    }

    public static Redo(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Cmd: kCellEditCommand | undefined = Self["_RedoStack"].pop();
            if (Cmd) {
                Self["_UndoStack"].push(Cmd);
                Cmd.Redo();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.Redo");
        }
    }

    /** Moved from kEditableTable.SetReadOnly (size split); the class member delegates here. */
    public static SetReadOnly(Self: kEditableTable, On: boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.ReadOnly = On;
            Self["_PaintReadOnlyMode"]();
            for (const CurrRow of Self.Rows.Items) { CurrRow.SetReadOnly(On); }
            if (Self.RowContextMenu !== null) { Self.SetupRowContextMenu(); }
            if (Self.CornerContextMenu !== null) { Self.SetupCornerContextMenu(); }
            // ⚠ ALL THREE MENUS, NOT TWO. The CELL menu (Paste / Fill down / Fill right) was left out
            // of this rebuild until 2026-08-26 — see SetupRangeContextMenu. Flipping the mode after
            // build would have left a read-only grid still offering the three write items.
            if (Self.CellsContextMenu !== null) { Self.SetupRangeContextMenu(); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.SetReadOnly");
        }
    }

    /** Moved from kEditableTable._PaintReadOnlyMode (size split); the class member delegates here. */
    public static _PaintReadOnlyMode(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.Table === null) { return; }
            if (Self.IsEffectivelyReadOnly() === true) {
                Self.Table.classList.add("kEditableTableReadOnly");
                return;
            }
            Self.Table.classList.remove("kEditableTableReadOnly");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle._PaintReadOnlyMode");
        }
    }

    /** Moved from kEditableTable.IsEffectivelyReadOnly (size split); the class member delegates here. */
    public static IsEffectivelyReadOnly(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (Self.ReadOnly === true) { return true; }
            if (Self.Columns.Items.length === 0) { return false; }
            for (const CurrCol of Self.Columns.Items) {
                if (CurrCol.Locked !== true) { return false; }
            }
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.IsEffectivelyReadOnly");
            return true;
        }
    }

    /** Moved from kEditableTable.BlankRowsAllowed (size split); the class member delegates here. */
    public static BlankRowsAllowed(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.IsEffectivelyReadOnly() === true) { return false; }
            return Self.AddBlankRows === true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.BlankRowsAllowed");
            return false;
        }
    }

    public static UpdateRowNumbers(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let CurrCell:kEditableTableCell;
            let RowNumber:number = 1;
            let CurrRow:kEditableTableRow;
            for(CurrRow of Self.Rows.Items){
                if(CurrRow.RowNumberCell){
                    CurrRow.Index = RowNumber -1;
                    CurrRow.SetRowNumberText(RowNumber.toString());
                    for(CurrCell of CurrRow.Cells){
                        CurrCell.RowIndex = RowNumber -1;
                    }
                }
                RowNumber++;
            }
            // Deletions/inserts route through here — keep the page window and
            // pager count in step (no-op unless filtering or pagination is on).
            Self._ReapplyIfFiltering();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.UpdateRowNumbers");
            return;
        }
    }

    public static ApplyAlternateRowShading(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!Self.AlternateRowShading) { return; }
            let RowIndex: number = 0;
            for (const CurrRow of Self.Rows.Items) {
                let BgColor: string = "";
                if (RowIndex % 2 === 1) { BgColor = Self.AlternateRowColor; }
                for (const CurrCell of CurrRow.Cells) {
                    if (CurrCell.Cell.style.backgroundColor !== "" && CurrCell.Cell.style.backgroundColor !== Self.AlternateRowColor) {
                        continue;
                    }
                    CurrCell.Cell.style.backgroundColor = BgColor;
                }
                RowIndex++;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.ApplyAlternateRowShading");
        }
    }

    // Overridden by kEditableTableRichHtml, which hosts a kJQXEditor popup.
    // The base grid deliberately has no jqWidgets dependency, so a RichHtml
    // column on a plain kEditableTable is a configuration error: report it
    // through the central handler instead of failing silently on click.
    public static OpenRichHtmlEditor(Self: kEditableTable, Cell: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            ks.KGlobalErrorHandler(
                new Error("Column '" + Cell.ColDef.Title + "' is RichHtml but this grid is a plain kEditableTable. Instantiate kEditableTableRichHtml (kEditableTableRichHtml.ts) instead."),
                "kEditableTable.OpenRichHtmlEditor");
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.OpenRichHtmlEditor");
        }
    }

    // Seam for the row form to embed a rich-text editor inline. The base grid
    // has no rich editor, so it returns null and the form falls back to a
    // delegate button. kEditableTableRichHtml overrides this to return a live
    // kJQXEditor host. Kept here (not on the subclass type) so the form can call
    // it through the shared kEditableTable reference.
    public static CreateRichHtmlInlineField(Self: kEditableTable, Cell: kEditableTableCell): kRowFormFieldControl | null {
        if (ks.KErrorMode === true) { return null; }
        try {
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.CreateRichHtmlInlineField");
            return null;
        }
    }

    public static AddColumn(Self: kEditableTable, Title:string, Type:kHTMLEditableColType, Width:number, PropName:string, DefaultValue?:any, IconImg?:HTMLImageElement): kEditableColumn {
        if (ks.KErrorMode === true) { return new kEditableColumn(); }
        try {
            const NewCol: kEditableColumn = new kEditableColumn(Title);
            
            NewCol.Title = Title;
            NewCol.Type = Type;
            NewCol.Width = Width;
            NewCol.KObjectPropertyName = PropName;
            if (DefaultValue) {
                NewCol.DefaultValue = DefaultValue;
            }
            NewCol.Icon = IconImg;
            Self.Columns.push(NewCol);
            return NewCol;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableLifecycle.AddColumn");
            return new kEditableColumn();
        }
    }
}
